import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, FileText, Download, Pencil, Trash2, Search, AlertCircle, Loader2, Building2, IndianRupee, Users, Calendar, Eye, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ContractFormModal } from './ContractFormModal';

const TYPE_LABEL = {
  managed_office: 'Managed Office',
  coworking: 'Coworking',
  virtual_office: 'Virtual Office',
};

const STATUS_BADGE = {
  active: 'bg-green-500/15 text-green-600 border-green-500/30',
  expired: 'bg-red-500/15 text-red-600 border-red-500/30',
  terminated: 'bg-gray-500/15 text-gray-500 border-gray-500/30',
  renewed: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
};

const expiryBadge = (days) => {
  if (days === null || days === undefined) return null;
  if (days < 0) return { text: `Lock-in expired ${Math.abs(days)}d ago`, cls: 'bg-red-500/15 text-red-600 border-red-500/30' };
  if (days <= 15) return { text: `${days}d to lock-in`, cls: 'bg-red-500/15 text-red-600 border-red-500/30' };
  if (days <= 30) return { text: `${days}d to lock-in`, cls: 'bg-orange-500/15 text-orange-600 border-orange-500/30' };
  if (days <= 60) return { text: `${days}d to lock-in`, cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30' };
  if (days <= 90) return { text: `${days}d to lock-in`, cls: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/30' };
  return null;
};

export const ContractsTab = ({ api, venues, userRole }) => {
  const [contracts, setContracts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [renewingFrom, setRenewingFrom] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [search, setSearch] = useState('');
  const [centreFilter, setCentreFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloading, setDownloading] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);

  const canManage = ['admin', 'hub_manager', 'center_manager', 'cluster_manager'].includes(userRole);
  const canExportAll = ['admin', 'hub_manager', 'finance', 'finance_audit'].includes(userRole);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get('/contracts'),
        api.get('/contracts/stats/summary'),
      ]);
      setContracts(Array.isArray(listRes.data) ? listRes.data : []);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete contract for "${c.client_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success('Contract deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete contract');
    }
  };

  const handleDownload = async (c) => {
    setDownloading(c.id);
    try {
      const res = await api.get(`/contracts/${c.id}/download`, { responseType: 'blob' });
      const ct = res.headers['content-type'] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: ct }));
      const a = document.createElement('a');
      a.href = url;
      a.download = c.document_filename || `Clayworks_Contract_${c.id.slice(0, 8)}`;
      // Firefox/Safari ignore clicks on detached anchors, and revoking immediately can cancel the download
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // With responseType 'blob' the error body is a Blob too — read the JSON detail out of it
      let detail = err.response?.data?.detail;
      if (!detail && err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch (_) { /* not JSON */ }
      }
      toast.error(detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const filtered = contracts.filter((c) => {
    if (centreFilter !== 'all' && c.centre_id !== centreFilter) return false;
    if (typeFilter !== 'all' && c.contract_type !== typeFilter) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.client_name || ''} ${c.centre_name || ''} ${c.building_name || ''} ${c.cabin_numbers || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="contracts-tab">
      {/* Header & action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading">Contract Management</h2>
          <p className="text-sm text-muted-foreground">Upload & track managed-office, coworking, and virtual-office contracts.</p>
        </div>
        <div className="flex gap-2">
          {canExportAll && (
            <Button
              variant="outline"
              onClick={async () => {
                setExportingAll(true);
                try {
                  const res = await api.get('/contracts/export/csv', { responseType: 'blob' });
                  const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `contracts_export_${Date.now()}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Contracts CSV downloaded');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Export failed');
                } finally { setExportingAll(false); }
              }}
              disabled={exportingAll}
              data-testid="contracts-export-csv-btn"
            >
              {exportingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export All (CSV)
            </Button>
          )}
          {canManage && (
            <Button onClick={() => { setEditing(null); setRenewingFrom(null); setShowForm(true); }} data-testid="contracts-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New Contract
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Total Contracts</p>
            <p className="text-2xl font-bold mt-1" data-testid="contracts-summary-total">{summary.total}</p>
            <p className="text-xs text-muted-foreground">{summary.active} active · {summary.expired} expired</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Monthly Revenue</p>
            <p className="text-2xl font-bold mt-1">₹{(summary.monthly_revenue || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-muted-foreground">across active contracts</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Lock-in expiring</p>
            <p className="text-2xl font-bold mt-1 text-amber-600" data-testid="contracts-summary-lockin">{summary.lockin_expiring_in_90d}</p>
            <p className="text-xs text-muted-foreground">in next 90 days</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> License ending</p>
            <p className="text-2xl font-bold mt-1 text-orange-600">{summary.expiring_in_90d}</p>
            <p className="text-xs text-muted-foreground">in next 90 days</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by client, centre, cabin…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contracts-search" />
        </div>
        <Select value={centreFilter} onValueChange={setCentreFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Centres" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Centres</SelectItem>
            {(venues || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[170px]"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="managed_office">Managed Office</SelectItem>
            <SelectItem value="coworking">Coworking</SelectItem>
            <SelectItem value="virtual_office">Virtual Office</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
            <SelectItem value="renewed">Renewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No contracts found.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const eb = expiryBadge(c.days_to_lockin_expiry);
            return (
              <Card key={c.id} data-testid={`contract-row-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-base truncate">{c.client_name}</h3>
                        <Badge className={`${STATUS_BADGE[c.status] || ''} border`} variant="outline">{c.status}</Badge>
                        <Badge variant="outline" className="text-xs">{TYPE_LABEL[c.contract_type] || c.contract_type}</Badge>
                        {eb && <Badge className={`${eb.cls} border text-xs`} variant="outline">{eb.text}</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.centre_name}{c.building_name ? ` · ${c.building_name}` : ''}{c.floor_number ? ` · Fl ${c.floor_number}` : ''}{c.cabin_numbers ? ` · Cabin ${c.cabin_numbers}` : ''}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.number_of_seats} seats</span>
                        <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{(c.monthly_license_fee || 0).toLocaleString('en-IN')}/mo</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Commence: <span className="font-mono">{c.license_commencement_date}</span> · Lock-in expiry: <span className="font-mono">{c.lock_in_expiry_date}</span> · License ends: <span className="font-mono">{c.license_end_date}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setViewing(c)} title="View full details" data-testid={`contract-view-${c.id}`}>
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDownload(c)} disabled={downloading === c.id} title="Download document" data-testid={`contract-download-${c.id}`}>
                        {downloading === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      </Button>
                      {canManage && (
                        <>
                          {c.status !== 'renewed' && (
                            <Button size="sm" variant="outline" onClick={() => { setRenewingFrom(c); setEditing(null); setShowForm(true); }} title="Renew contract" data-testid={`contract-renew-${c.id}`}>
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => { setEditing(c); setRenewingFrom(null); setShowForm(true); }} title="Edit contract" data-testid={`contract-edit-${c.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(c)} className="text-red-600 hover:bg-red-500/10" title="Delete" data-testid={`contract-delete-${c.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ContractFormModal
        open={showForm}
        onClose={(v) => { setShowForm(v); if (!v) { setEditing(null); setRenewingFrom(null); } }}
        api={api}
        venues={venues}
        editing={editing}
        renewingFrom={renewingFrom}
        onSaved={fetchData}
      />
      <ContractDetailsModal
        contract={viewing}
        contracts={contracts}
        onClose={() => setViewing(null)}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </div>
  );
};

const ContractRow = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/40 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className={`text-right break-words ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</span>
  </div>
);

/* -------------------- View modal: full contract metadata -------------------- */
const ContractDetailsModal = ({ contract, contracts, onClose, onDownload, downloading }) => {
  if (!contract) return null;
  const c = contract;
  const Row = ContractRow;
  const renewedFrom = c.renewed_from_contract_id && (contracts || []).find((x) => x.id === c.renewed_from_contract_id);
  const renewedTo = c.renewed_to_contract_id && (contracts || []).find((x) => x.id === c.renewed_to_contract_id);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="contract-details-modal">
      <div className="bg-background rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">{c.client_name}</h3>
            <p className="text-xs text-muted-foreground">Contract Metadata</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onDownload(c)} disabled={downloading === c.id} data-testid="contract-details-download">
              {downloading === c.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Document
            </Button>
            <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-4rem)]">
          <div className="grid md:grid-cols-2 gap-x-6">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-2 mb-1">Party & Type</p>
              <Row label="Client" value={c.client_name} />
              <Row label="GSTIN" value={c.client_gstin} mono />
              <Row label="PAN" value={c.client_pan} mono />
              <Row label="Contract Type" value={TYPE_LABEL[c.contract_type] || c.contract_type} />
              <Row label="Status" value={c.status} />
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-1">Location</p>
              <Row label="Centre" value={c.centre_name} />
              <Row label="Building" value={c.building_name} />
              <Row label="Floor" value={c.floor_number} />
              <Row label="Cabin(s)" value={c.cabin_numbers} />
              <Row label="Seats" value={c.number_of_seats} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-2 mb-1">Financials</p>
              <Row label="Monthly License Fee" value={c.monthly_license_fee ? `₹${Number(c.monthly_license_fee).toLocaleString('en-IN')}` : '—'} />
              <Row label="Security Deposit" value={c.security_deposit ? `₹${Number(c.security_deposit).toLocaleString('en-IN')}` : '—'} />
              <Row label="Annual Escalation" value={c.annual_escalation_percent != null ? `${c.annual_escalation_percent}%` : '—'} />
              <Row label="Billing Cycle" value={c.billing_cycle} />
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-1">Dates</p>
              <Row label="Commencement" value={c.license_commencement_date} mono />
              <Row label="Lock-in Ends" value={c.lock_in_end_date} mono />
              <Row label="Lock-in Expiry" value={c.lock_in_expiry_date} mono />
              <Row label="License Ends" value={c.license_end_date} mono />
              <Row label="Signed" value={c.signed_date} mono />
              <Row label="Renewal Notice" value={c.renewal_notice_days ? `${c.renewal_notice_days} days` : '—'} />
            </div>
          </div>
          {(renewedFrom || renewedTo || c.renewed_from_contract_id || c.renewed_to_contract_id) && (
            <>
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-1">Renewal History</p>
              {c.renewed_from_contract_id && (
                <Row label="Renewed from" value={renewedFrom ? `${renewedFrom.client_name} (ended ${renewedFrom.license_end_date})` : c.renewed_from_contract_id.slice(0, 12)} />
              )}
              {c.renewed_to_contract_id && (
                <Row label="Renewed into" value={renewedTo ? `${renewedTo.client_name} (from ${renewedTo.license_commencement_date})` : c.renewed_to_contract_id.slice(0, 12)} />
              )}
            </>
          )}
          {c.notes && (
            <>
              <p className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-1">Notes</p>
              <p className="text-sm whitespace-pre-wrap bg-muted/30 p-3 rounded border">{c.notes}</p>
            </>
          )}
          <div className="mt-4 pt-3 border-t text-xs text-muted-foreground grid grid-cols-2 gap-2">
            <div>ID: <span className="font-mono">{c.id?.slice(0, 12)}</span></div>
            <div>Document: {c.document_filename || '—'}</div>
            <div>Created: <span className="font-mono">{(c.created_at || '').slice(0, 10)}</span></div>
            <div>Updated: <span className="font-mono">{(c.updated_at || '').slice(0, 10)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractsTab;
